const navJSON = (rawItems, activityTypes) => {
    const flatItems = typeof rawItems === 'string' ? JSON.parse(rawItems) : rawItems;
    activityTypes = typeof activityTypes === 'string' ? JSON.parse(activityTypes) : activityTypes;

    const toNode = (item, isChild = false) => {
        const node = {
            id: item.id,
            type: item.type,
            label: item.label,
            icon: item.icon,
            ...(item.path != null ? { path: item.path } : {}),
            system: item.system,
            position: item.position,
            permission: item.permission === 'NULL' ? null : item.permission.split(','),
            ...(item.permissionOp ? { condition: item.permissionOp } : null),
            module: item.module ?? null,
            ...(item.childrenSourse ? { childrenSourse: item.childrenSourse } : {}),
        };
        if (!isChild) node.condition = item.condition ?? null;
        return node;
    };

    const byParent = flatItems.reduce((acc, item) => {
        if (item.parentId) (acc[item.parentId] ??= []).push(item);
        return acc;
    }, {});

    return flatItems
        .filter(item => !item.parentId)
        .sort((a, b) => a.position - b.position)
        .map(item => {
            const node = toNode(item);
            const children = byParent[item.id];
            if (children?.length) {
                node.children = children
                    .sort((a, b) => a.position - b.position)
                    .map(child => toNode(child, true));
            }
            if (item.childrenSourse === 'activityTypes') {
                node.children = activityTypes.map((t, i) => ({
                    id: `activities-${t.slug}`,
                    type: 'link',
                    label: t.label,
                    path: `/activities/${t.slug}`,
                    position: i,
                    icon: t.icon
                }));
            }
            return node;
        });
}

const DEFAULT_NAV_ITEMS = [
    {
        id: 'home', type: 'link', label: 'Home', icon: 'HomeIcon',
        path: '/', system: true, position: 0,
        permission: null, module: null, condition: null,
    },
    {
        id: 'logs', type: 'submenu', label: 'Logs', icon: 'LayersIcon',
        system: true, position: 1, permission: null, module: null, condition: null,
        children: [
            { id: 'logs-add', type: 'link', label: 'Log new dives', icon: 'WavesIcon', path: '/logs/add', system: true, position: 0, permission: null, module: null },
            { id: 'logs-history', type: 'link', label: 'My dive log history', icon: 'ListIcon', path: '/logs/history', system: true, position: 1, permission: null, module: null },
            { id: 'logs-incidents', type: 'link', label: 'Log incidents', icon: 'AlertIcon', path: '/logs/incidents', system: true, position: 2, permission: 'Incident', module: null },
            { id: 'logs-import', type: 'link', label: 'Import logs', icon: 'UploadIcon', path: '/logs/import', system: true, position: 3, permission: 'Import', module: null },
        ],
    },
    {
        id: 'schedule', type: 'link', label: 'Schedule dives', icon: 'CalendarIcon',
        path: '/schedule', system: true, position: 2,
        permission: 'Schedule', module: 'Schedule', condition: 'exhibitDiver',
    },
    {
        id: 'dive-plans', type: 'submenu', label: 'Dive plans', icon: 'LayersIcon2',
        system: true, position: 3, permission: null, module: null, condition: null,
        children: [
            { id: 'dive-plans-add', type: 'link', label: 'New dive plan', icon: 'FolderIcon', path: '/dive-plans/add', system: true, position: 0, permission: null, module: null },
            { id: 'dive-plans-history', type: 'link', label: 'Dive plan history', icon: 'ListIcon', path: '/dive-plans/history', system: true, position: 1, permission: null, module: null },
        ],
    },
    {
        id: 'reports', type: 'submenu', label: 'Reports', icon: 'ChartIcon',
        system: true, position: 4, permission: null, permissionOp: 'AND', module: null, condition: null,
        children: [
            { id: 'reports-expirations', type: 'link', label: 'Expirations', icon: 'CalendarIcon', path: '/reports/expirations', system: true, position: 0, permission: 'Report', module: null },
            { id: 'reports-aaus', type: 'link', label: 'AAUS Report', icon: 'Chart1Icon', path: '/reports/aaus', system: true, position: 2, permission: 'Report', module: null },
            { id: 'reports-internal', type: 'link', label: 'Internal Stats Report', icon: 'Chart2Icon', path: '/reports/internal', system: true, position: 3, permission: 'Report', module: null },
            { id: 'reports-attendance', type: 'link', label: 'Attendance Report', icon: 'UserCheckIcon', path: '/reports/attendance', system: true, position: 3, permission: 'Report', module: 'Schedule' },
            { id: 'reports-feedings', type: 'link', label: 'Feedings Report', icon: 'Chart3Icon', path: '/reports/feedings', system: true, position: 4, permission: null, module: 'Feedings' },
            { id: 'reports-comm-problems', type: 'link', label: 'Comm Problems', icon: 'AnnotationAlert', path: '/reports/comm-problems', system: true, position: 5, permission: null, module: 'Dive incidents' },
        ],
    },
    {
        id: 'activities', type: 'submenu', label: 'Activities', icon: 'OctagonIcon',
        system: true, position: 5, permission: null, module: 'Activities', condition: null,
        childrenSourse: 'activityTypes',
    },
    {
        id: 'volunteer', type: 'submenu', label: 'Contact Management', icon: 'UsersCheckIcon',
        system: true, position: 6, permission: 'Edit volunteer', module: null, condition: null,
        children: [
            { id: 'volunteer-teams', type: 'link', label: 'Teams', icon: 'Users3Icon', path: '/volunteer/teams', system: true, position: 0, permission: 'Edit volunteer', module: 'Schedule' },
            { id: 'contacts', type: 'link', label: 'Contacts', icon: 'MailIcon', path: '/volunteer/contacts', system: true, position: 1, permission: 'View user', module: null },
        ],
    },
    {
        id: 'configuration', type: 'submenu', label: 'Configuration', icon: 'UsersIcon',
        system: true, position: 7,
        permission: ['View user', 'Locations', 'Tasks', 'Pages'], permissionOp: 'OR',
        module: null, condition: null,
        children: [
            { id: 'configuration-users', type: 'link', label: 'Users', icon: 'UserIcon', path: '/configuration/users', system: true, position: 0, permission: 'View user', module: null },
            { id: 'configuration-locations', type: 'link', label: 'Locations', icon: 'GlobeIcon', path: '/configuration/locations', system: true, position: 1, permission: 'Locations', module: null },
            { id: 'configuration-tasks', type: 'link', label: 'Dive log tasks', icon: 'TargetIcon', path: '/configuration/tasks', system: true, position: 2, permission: 'Tasks', module: null },
            { id: 'configuration-pages', type: 'link', label: 'Custom pages', icon: 'HandIcon', path: '/configuration/pages', system: true, position: 3, permission: 'Pages', module: 'Pages' },
            { id: 'configuration-dive-modes', type: 'link', label: 'Dive modes', icon: 'DiveModeIcon', path: '/configuration/dive-modes', system: true, position: 4, permission: 'Dive modes', module: 'Dive modes' },
            { id: 'configuration-om', type: 'link', label: 'OM settings', icon: 'SettingsIcon', path: '/configuration/om', system: true, position: 4, permission: 'OM settings', module: 'OM settings' },
        ],
    },
    {
        id: 'suggestion-box', type: 'link', label: 'Suggestion box', icon: 'MesIcon',
        path: '/suggestion-box', system: true, position: 7,
        permission: null, module: null, condition: null,
    },
];

const defaultNav = (activityTypes) => {
    const types = typeof activityTypes === 'string' ? JSON.parse(activityTypes) : (activityTypes || []);
    return DEFAULT_NAV_ITEMS.map(item => {
        if (item.childrenSourse === 'activityTypes') {
            return {
                ...item,
                children: types.map((t, i) => ({
                    id: `activities-${t.slug}`,
                    type: 'link',
                    label: t.label,
                    path: `/activities/${t.slug}`,
                    position: i,
                    icon: t.icon
                })),
            };
        }
        return item;
    });
};

export const navService = {
    navJSON,
    defaultNav,
    DEFAULT_NAV_ITEMS
};